import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { initAnalytics, trackPageView } from "./lib/analytics";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { BrandProvider } from "@/context/BrandContext";
import { AuthCallback } from "@/components/AuthCallback";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import Landing from "@/pages/Landing";
import AuthPage from "@/pages/AuthPage";
import Onboarding from "@/pages/Onboarding";
import ParentDashboard from "@/pages/ParentDashboard";
import { PrivacyPage, TermsPage } from "@/pages/LegalPages";
import { ParentPinGuard } from "@/components/ParentPinGuard";

// Code-splitting: heavy surfaces (3D coach, math tutor, admin, opportunities)
// load on demand so first paint on slow connections stays fast.
const ChildShell = lazy(() => import("@/pages/ChildShell"));
const HomeworkTutorPage = lazy(() => import("@/pages/HomeworkTutorPage"));
const ExamPrepPage = lazy(() => import("@/pages/ExamPrepPage"));
const MovieModePage = lazy(() => import("@/pages/MovieModePage"));
const AdminPanel = lazy(() => import("@/pages/AdminPanel"));
const MemoryConsole = lazy(() => import("@/pages/MemoryConsole"));
const GrowthDashboard = lazy(() => import("@/pages/GrowthDashboard"));
const SharedStory = lazy(() => import("@/pages/SharedStory"));
const SharedClip = lazy(() => import("@/pages/SharedClip"));
const SharedOpportunities = lazy(() => import("@/pages/SharedOpportunities"));
const ProDashboard = lazy(() => import("@/pages/ProDashboard"));
const KitPage = lazy(() => import("@/pages/KitPage"));
const KitCatalogPage = lazy(() => import("@/pages/KitCatalogPage"));
const KitOrderResult = lazy(() => import("@/pages/KitOrderResult"));
const OrderTrackingPage = lazy(() => import("@/pages/OrderTrackingPage"));
const ReceiptPage = lazy(() => import("@/pages/ReceiptPage"));
const OpportunitiesPage = lazy(() => import("@/pages/OpportunitiesPage"));
const AssistantPage = lazy(() => import("@/pages/AssistantPage"));
const RecapPrintPage = lazy(() => import("@/pages/RecapPrintPage"));
const SchoolDashboard = lazy(() => import("@/pages/SchoolDashboard"));
const CreatorDashboard = lazy(() => import("@/pages/CreatorDashboard"));
const MathTutorPage = lazy(() => import("@/pages/MathTutorPage"));
const MathHub = lazy(() => import("@/pages/MathHub"));
const MathMasteryHub = lazy(() => import("@/pages/MathMasteryHub"));
const LearnSessionPage = lazy(() => import("@/pages/LearnSessionPage"));
const CodeLabPage = lazy(() => import("@/pages/CodeLabPage"));
const OpportunityCornerPage = lazy(() => import("@/pages/OpportunityCornerPage"));
const BadgeCabinetPage = lazy(() => import("@/pages/BadgeCabinetPage"));
const BridgeCertificate = lazy(() => import("@/pages/BridgeCertificate"));
const SharedCertificate = lazy(() => import("@/pages/SharedCertificate"));
const SpellingBee = lazy(() => import("@/pages/SpellingBee"));
const CVCBlender = lazy(() => import("@/pages/CVCBlender"));
const VowelSoccer = lazy(() => import("@/pages/VowelSoccer"));
const SentenceArchitect = lazy(() => import("@/pages/SentenceArchitect"));
const SyntaxGlitch = lazy(() => import("@/pages/SyntaxGlitch"));
const WordFlashcards = lazy(() => import("@/pages/WordFlashcards"));
const HiveMap = lazy(() => import("@/pages/HiveMap"));
const CoverStudio = lazy(() => import("@/pages/CoverStudio"));
const StoryStudio = lazy(() => import("@/pages/StoryStudio"));
const SharedAudiobook = lazy(() => import("@/pages/SharedAudiobook"));
const MathGlitchBoss = lazy(() => import("@/pages/MathGlitchBoss"));
const BillingPage = lazy(() => import("@/pages/BillingPage"));
const PaymentSuccess = lazy(() =>
  import("@/pages/BillingPage").then((m) => ({ default: m.PaymentSuccess })));
const PaymentCancel = lazy(() =>
  import("@/pages/BillingPage").then((m) => ({ default: m.PaymentCancel })));

const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="h-8 w-8 rounded-full border-4 border-primary border-t-transparent animate-spin"
      role="status" aria-label="Loading" />
  </div>
);

function AppRouter() {
  const location = useLocation(); // read hash from here, not window.location.hash (not reactive)
  useEffect(() => {
    initAnalytics();
    trackPageView(location.pathname);
  }, [location.pathname]);
  // Emergent OAuth returns with #session_id= in the URL fragment — process it first.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <Onboarding />
          </ProtectedRoute>
        }
      />
      <Route
        path="/homework-tutor"
        element={
          <ProtectedRoute>
            <HomeworkTutorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/exam-prep"
        element={
          <ProtectedRoute>
            <ExamPrepPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/movie-mode"
        element={
          <ProtectedRoute>
            <MovieModePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <ParentDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/:section"
        element={
          <ProtectedRoute>
            <ParentDashboard />
          </ProtectedRoute>
        }
      />
      <Route path="/child" element={<Navigate to="/dashboard" replace />} />
      <Route
        path="/child/:childId"
        element={
          <ProtectedRoute>
            <ChildShell />
          </ProtectedRoute>
        }
      />
      <Route
        path="/memory/:childId"
        element={
          <ProtectedRoute>
            <MemoryConsole />
          </ProtectedRoute>
        }
      />
      <Route
        path="/growth/:childId"
        element={
          <ProtectedRoute>
            <GrowthDashboard />
          </ProtectedRoute>
        }
      />
      <Route path="/shared/:token" element={<SharedStory />} />
      <Route path="/shared-clip/:token" element={<SharedClip />} />
      <Route path="/cert/:token" element={<SharedCertificate />} />
      <Route path="/opportunities/shared/:shareId" element={<SharedOpportunities />} />
      <Route
        path="/kit/:code"
        element={
          <ProtectedRoute>
            <KitPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/kits"
        element={
          <ProtectedRoute>
            <KitCatalogPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/opportunities"
        element={
          <ProtectedRoute>
            <ParentPinGuard>
              <OpportunitiesPage />
            </ParentPinGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/kits/order-result"
        element={
          <ProtectedRoute>
            <KitOrderResult />
          </ProtectedRoute>
        }
      />
      <Route
        path="/orders/:orderId/track"
        element={
          <ProtectedRoute>
            <OrderTrackingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/orders/:orderId/receipt"
        element={
          <ProtectedRoute>
            <ReceiptPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/recap/print"
        element={
          <ProtectedRoute>
            <RecapPrintPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/assistant"
        element={
          <ProtectedRoute>
            <ParentPinGuard>
              <AssistantPage />
            </ParentPinGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/pro"
        element={
          <ProtectedRoute>
            <ProDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/school"
        element={
          <ProtectedRoute>
            <SchoolDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/creator"
        element={
          <ProtectedRoute>
            <CreatorDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing"
        element={
          <ProtectedRoute>
            <ParentPinGuard>
              <BillingPage />
            </ParentPinGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/math"
        element={
          <ProtectedRoute>
            <MathTutorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/math-hub"
        element={
          <ProtectedRoute>
            <MathHub />
          </ProtectedRoute>
        }
      />
      <Route
        path="/math-mastery"
        element={
          <ProtectedRoute>
            <MathMasteryHub />
          </ProtectedRoute>
        }
      />
      <Route
        path="/learn-session"
        element={
          <ProtectedRoute>
            <LearnSessionPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/code-lab"
        element={
          <ProtectedRoute>
            <CodeLabPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/opportunity-corner"
        element={
          <ProtectedRoute>
            <OpportunityCornerPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/badge-cabinet"
        element={
          <ProtectedRoute>
            <BadgeCabinetPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/math-mastery/certificate/:courseId"
        element={
          <ProtectedRoute>
            <BridgeCertificate />
          </ProtectedRoute>
        }
      />
      <Route
        path="/spelling-bee"
        element={
          <ProtectedRoute>
            <SpellingBee />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cvc-blender"
        element={
          <ProtectedRoute>
            <CVCBlender />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vowel-soccer"
        element={
          <ProtectedRoute>
            <VowelSoccer />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sentence-architect"
        element={
          <ProtectedRoute>
            <SentenceArchitect />
          </ProtectedRoute>
        }
      />
      <Route
        path="/syntax-glitch"
        element={
          <ProtectedRoute>
            <SyntaxGlitch />
          </ProtectedRoute>
        }
      />
      <Route
        path="/word-flashcards"
        element={
          <ProtectedRoute>
            <WordFlashcards />
          </ProtectedRoute>
        }
      />
      <Route
        path="/hive-map"
        element={
          <ProtectedRoute>
            <HiveMap />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cover-studio"
        element={
          <ProtectedRoute>
            <CoverStudio />
          </ProtectedRoute>
        }
      />
      <Route
        path="/story-studio"
        element={
          <ProtectedRoute>
            <StoryStudio />
          </ProtectedRoute>
        }
      />
      <Route path="/vault/shared/:token" element={<SharedAudiobook />} />
      <Route
        path="/math-boss"
        element={
          <ProtectedRoute>
            <MathGlitchBoss />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment/success"
        element={
          <ProtectedRoute>
            <PaymentSuccess />
          </ProtectedRoute>
        }
      />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route
        path="/admin"
        element={
          <ProtectedRoute adminOnly>
            <AdminPanel />
          </ProtectedRoute>
        }
      />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <BrandProvider>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </BrandProvider>
    </BrowserRouter>
  );
}

export default App;
